import React, { useEffect, useRef } from 'react';
import Menu from 'storybookConfig/stories/cells/Menu';
import Icon from 'storybookConfig/stories/molecules/Icon';
import { type RootState, useAppDispatch, useAppSelector } from 'store';
import Categories from 'components/Dashboard/LeftMenu/Categories';
import { closeCategories, openCategories } from 'store/slices/sidebar';
import Body from 'storybookConfig/stories/molecules/Body';
import { SubLinkContainer } from './Shared.styles';
import useLoadCategories, { type CategoryNode } from 'hooks/useLoadCategories';

interface MarketplaceLinksProps {
  isActive: (path: string) => boolean;
}

const MarketplaceLinks = ({ isActive }: MarketplaceLinksProps) => {
  const [expanded, setExpanded] = React.useState(true);
  const [categoriesExpanded, setCategoriesExpanded] = React.useState(false);
  const dispatch = useAppDispatch();
  const categoriesRef = useRef<HTMLDivElement | null>(null);
  const { categoriesOpen } = useAppSelector((state: RootState) => state.sidebar);
  const { categories }: { categories: CategoryNode } = useLoadCategories();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target as Node)) {
        dispatch(closeCategories());
      }
    };

    if (categoriesOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [categoriesOpen, dispatch]);

  return (
    <>
      <Menu.LinkButton
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <Body variant="Body/Header">Marketplace</Body>
        <Icon name={expanded ? 'arrow_drop_up' : 'arrow_drop_down'} />
      </Menu.LinkButton>
      {expanded && (
        <SubLinkContainer>
          <Menu.Link
            $isActive={isActive('/marketplace/home/featured')}
            $iconName="home"
            to="/marketplace/home/featured"
          >
            Home
          </Menu.Link>
          <Menu.LinkButton
            $iconName="apps"
            $isActive={isActive('/marketplace/category/:id')}
            onClick={() => {
              setCategoriesExpanded(!categoriesExpanded);
            }}
          >
            Categories
            <Icon name={categoriesExpanded ? 'arrow_drop_up' : 'arrow_drop_down'} />
          </Menu.LinkButton>
          {categoriesExpanded && (
            <SubLinkContainer customMarginLeft="24px" borderLeft>
              <Menu.Link to="/marketplace/category/0">All</Menu.Link>
              <Menu.Link to="/marketplace/category/1">Animals & Pets</Menu.Link>
              <Menu.Link to="/marketplace/category/166">Apparel</Menu.Link>
              <Menu.LinkButton
                onClick={() => {
                  dispatch(openCategories());
                }}
              >
                See More
              </Menu.LinkButton>
            </SubLinkContainer>
          )}
          <Menu.Link
            $isActive={isActive('/marketplace/home/favorites')}
            $iconName="favorite"
            to="/marketplace/home/favorites"
          >
            Favorites
          </Menu.Link>
          <Menu.Link
            $isActive={isActive('/marketplace/home/recommendations')}
            $iconName="post_add"
            to="/marketplace/home/recommendations"
          >
            Recommendations
          </Menu.Link>
        </SubLinkContainer>
      )}

      {categoriesOpen && (
        <div ref={categoriesRef}>
          <Categories data={categories} />
        </div>
      )}
    </>
  );
};

export default MarketplaceLinks;
